<template>
  <div
    v-if="mjmlStore.initialLoaded"
    id="app"
    class="lower-zindex"
    data-testid="main-app"
    :data-mode="generalStore.light ? 'light' : 'dark'"
    :class="{
      preview: previewMode,
      noselect: draggingSlider || resizingColumns,
      withoutTopBar: removeTopBar,
      'light-theme': generalStore.light,
      'dark-theme': !generalStore.light,
    }"
  >
    <developer-template-modal></developer-template-modal>

    <TemplatePickerModal @setChoice="initState = false"></TemplatePickerModal>

    <notifications />

    <WindowBar> </WindowBar>
    <ToggleControl
      v-if="layoutStore.showControlPanelExpand && layoutStore.hideControlPanel"
      data-testid="expand-control-panel"
    />
    <div style="display: flex">
      <div
        v-show="!previewMode && !layoutStore.hideControlPanel"
        id="controlPanel"
        class="fixed bottom-0 top-0 z-[1501] min-h-[100vh] w-[310px] border-0 border-r border-solid border-gray-500 bg-gray-700"
        :class="{
          '!top-[32px]': topBarStore.showWindowBar,
          '!border-r-0 border-l': alignRight,
        }"
        :style="leftPaneAlignRight"
      >
        <ToggleControl
          v-if="layoutStore.showControlPanelExpand"
          data-testid="collapse-control-panel"
        />
        <PerfectScrollbar
          ref="scroll"
          class="scroll-area"
          :watch-options="true"
          :options="{
            wheelPropagation: visibleFileManager,
          }"
        >
          <div ref="scrollContent">
            <propagation-bar
              v-if="type === 'standalone'"
              class="pb-5"
            ></propagation-bar>
            <menu-header
              v-if="!topBarStore.showWindowBar && type !== 'standalone'"
            ></menu-header>
            <t-tabs
              ref="tabs"
              class="pb-11"
              :show-window-bar="topBarStore.showWindowBar"
              @changed="clearSelection"
            >
              <t-tab
                key="content"
                :tab-title="$t('tabs.content')"
                class="pr-1.5"
              >
                <div v-if="!activeBlock" class="pt-5">
                  <div class="px-2.5">
                    <TTabsV2
                      :options="contentOptionsNavigation"
                      :defult-active-index="contentTabActiveIndex()"
                      @on-tab-change="onContentTabChanged"
                    ></TTabsV2>
                  </div>

                  <div class="mt-4 px-2.5">
                    <content-boxes-container
                      v-if="selected === 'basic'"
                    ></content-boxes-container>
                    <premade-container
                      v-if="
                        selected === 'premade' &&
                        (premadeBlocksStore.premadeBlocks !== false ||
                          premadeTemplates.premadeTemplates !== false)
                      "
                    ></premade-container>
                    <saved-container
                      v-if="
                        selected === 'saved' &&
                        (savedBlocksStore.savedBlocks || enableSyncedSections)
                      "
                    ></saved-container>
                  </div>
                </div>
                <div v-else>
                  <blockForm></blockForm>
                </div>
              </t-tab>
              <t-tab
                key="structure"
                :tab-title="$t('tabs.structure')"
                class="pr-1.5"
              >
                <section-form
                  v-if="activeSection"
                  :key="activeSection.sectionIndex"
                  :section="activeSection"
                ></section-form>
                <structures-container v-else />
              </t-tab>
              <t-tab
                v-if="!hideSettingsTab"
                key="settings"
                :tab-title="$t('tabs.settings')"
                class="pr-1.5"
              >
                <settings-form></settings-form>
              </t-tab>
            </t-tabs>
          </div>
        </PerfectScrollbar>
      </div>
      <div id="rightPane" class="editor-view" :style="rightPaneAlign">
        <top-bar v-if="!removeTopBar"></top-bar>
        <StartChoice
          v-if="
            premadeTemplates.premadeTemplates &&
            JSON.stringify(getMjml) ===
              JSON.stringify(mjmlStore.defaultTemplate) &&
            initState
          "
          @setChoice="initState = false"
        />
        <email v-else></email>
      </div>
    </div>
    <ImageEditorV2
      v-if="photoEditorImage"
      ref="imageEditor"
      style="position: fixed; width: 100%; z-index: 15999; top: 0px"
    ></ImageEditorV2>
    <BlockLanguageSelector ref="blockLanguageSelector" />
    <UiBlockedOverlay />
  </div>
</template>

<script lang="ts" setup>
  import email from "@/layout/email.vue";
  import TopBar from "@/layout/TopBar/TopBar.vue";
  import WindowBar from "@/layout/TopBar/WindowBar.vue";
  import DeveloperTemplateModal from "@/layout/developerTemplateModal.vue";
  import StartChoice from "@/layout/StartChoice/StartChoice.vue";
  import TemplatePickerModal from "@/layout/StartChoice/TemplatePickerModal.vue";

  import settingsForm from "@/forms/settingsForm.vue";
  import blockForm from "@/forms/blockForm.vue";
  import ContentBoxesContainer from "@/forms/components/ContentBoxesContainer/ContentBoxesContainer.vue";
  import SavedContainer from "@/forms/components/SavedContainer/SavedContainer.vue";
  import StructuresContainer from "@/forms/components/StructuresContainer/StructuresContainer.vue";
  import PremadeContainer from "@/forms/components/PremadeContainer/PremadeContainer.vue";
  import sectionForm from "@/forms/sectionForm.vue";
  import Notifications from "@/forms/components/Notifications/Notifications.vue";
  import PropagationBar from "@/forms/components/PropagationBar/PropagationBar.vue";
  import MenuHeader from "@/forms/components/MenuHeader/MenuHeader.vue";
  import ImageEditorV2 from "@/forms/elements/imageEditorV2.vue";

  import useShortcuts from "@/composables/useShortcuts";
  import useApiAuthorizationHeader from "@/composables/useApiAuthorizationHeader";
  import useLoad from "@/composables/useLoad";
  import useUpdateTemplate from "@/composables/useUpdateTemplate";
  import useComments from "@/composables/useComments";
  import useSyncedSections from "@/composables/useBlocksFromAPI/useSyncedSections";

  import { useNotificationsStore } from "@/stores/notifications";
  import { useTopBarStore } from "@/stores/topBar";
  import { usePreviewStore } from "@/stores/preview";
  import { useLayoutStore } from "@/stores/layoutStore";
  import { useDraggingStore } from "@/stores/draggingStore";
  import { useResizeColumnsStore } from "@/stores/resizeColumnsStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useImageEditorStore } from "@/stores/imageEditorStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useOpenAiStore } from "@/stores/openAiStore";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
  import { usePremadeBlocksStore } from "@/stores/premadeBlocksStore";
  import { usePremadeTemplatesStore } from "@/stores/premadeTemplatesStore";
  import { useFileManagerStore } from "@/stores/fileManger";
  import { defaultTemplate } from "@/store/defaultTemplate";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useChatAI } from "./stores/chatAIStore";
  import { useAutosavesStore } from "@/stores/autosaves";
  import { useCommentsStore } from "@/stores/commentsStore";

  import superEncodeURI from "@/utils/superEncodeURI";
  import useActions from "@/actions/useActions";
  import { setOptions, IOptions } from "@/init/setInitState";

  import INotification from "types/Notification/INotification";
  import { IMergeTagArray } from "@/schemas/mergeTags/mergeTagsSchema";
  import { SelectPrimaryBlockLanguage } from "@/schemas/template/languageSchema";

  import { TTabs, TTab, TTabsV2 } from "@topol/twig-ui";

  import { useI18n } from "vue-i18n";
  import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
  import BlockLanguageSelector from "./layout/BlockLanguageSelector/BlockLanguageSelector.vue";
  import UiBlockedOverlay from "./layout/UiBlockedOverlay/UiBlockedOverlay.vue";
  import ToggleControl from "./layout/ToggleControl/ToggleControl.vue";

  const activeTab = ref("blocks");
  const selected = ref("basic");
  const initState = ref(true);
  const tabs = ref();
  const scroll = ref();
  const scrollContent = ref<HTMLElement>();
  let scrollResizeObserver: ResizeObserver | null = null;
  const blockLanguageSelector = ref();

  useShortcuts();

  const notificationsStore = useNotificationsStore();
  const topBarStore = useTopBarStore();
  const previewStore = usePreviewStore();
  const layoutStore = useLayoutStore();
  const draggingStore = useDraggingStore();
  const resizeColumnsStore = useResizeColumnsStore();
  const generalStore = useGeneralStore();
  const imageEditorStore = useImageEditorStore();
  const mjmlStore = useMjmlStore();
  const openAiStore = useOpenAiStore();
  const savedBlocksStore = useSavedBlocksStore();
  const premadeBlocksStore = usePremadeBlocksStore();
  const premadeTemplates = usePremadeTemplatesStore();
  const fileManagerStore = useFileManagerStore();
  const mergeTagsStore = useMergeTagsStore();
  const chatAIStore = useChatAI();
  const autosavesStore = useAutosavesStore();
  const commentsStore = useCommentsStore();
  const { t } = useI18n();

  const { save } = useActions();
  const { loadJson } = useLoad();
  const { updateTemplate } = useUpdateTemplate();
  const { refreshComments } = useComments();
  const { updateSyncedSections } = useSyncedSections();
  const { setApiAuthorizationHeader } = useApiAuthorizationHeader();

  const removeTopBar = computed(() => {
    if (topBarStore.removeTopBar) {
      return true;
    }
    if (
      previewStore.previewMode &&
      !topBarStore.showPreviewTopBar &&
      !topBarStore.showMainTopBar
    ) {
      return true;
    }
    if (!previewStore.previewMode && !topBarStore.showMainTopBar) {
      return true;
    }
    return false;
  });

  const scrollHeight = computed(() => {
    if (topBarStore.showWindowBar) {
      return "calc(100vh - 32px)";
    }

    return "100vh";
  });

  const photoEditorImage = computed(() => {
    return (
      imageEditorStore.enabled &&
      (imageEditorStore.photoEditorImage || imageEditorStore.createNew)
    );
  });

  const activeBlock = computed(() => {
    if (mjmlStore.selectedBlock) {
      changeActiveTab(0);
    }

    return mjmlStore.selectedBlock;
  });

  const activeSection = computed(() => {
    if (mjmlStore.selectedSection && mjmlStore.getSelectedSectionByIndex()) {
      changeActiveTab(1);
      return mjmlStore.selectedSection;
    }

    return false;
  });

  const previewMode = computed(() => {
    return previewStore.previewMode;
  });

  const draggingSlider = computed(() => {
    return draggingStore.draggingSlider;
  });

  // Dragging the column handle would otherwise sweep a text selection across
  // the canvas, same as dragging a slider.
  const resizingColumns = computed(() => {
    return resizeColumnsStore.isResizing;
  });

  const type = computed(() => {
    return generalStore.type;
  });

  const getMjml = computed(() => {
    return mjmlStore.mjml;
  });

  const alignRight = computed(() => {
    return layoutStore.mainMenuAlign === "right";
  });

  const rightPaneAlign = computed(() => {
    if (previewMode.value || layoutStore.hideControlPanel === true) {
      return {
        marginLeft: "0px",
        marginRight: "0px",
      };
    } else {
      return {
        marginLeft: alignRight.value ? "0px" : "310px",
        marginRight: alignRight.value ? "310px" : "0px",
      };
    }
  });

  const leftPaneAlignRight = computed(() => {
    return {
      right: alignRight.value ? "0" : "auto",
    };
  });

  const hideSettingsTab = computed(() => {
    return layoutStore.hideSettingsTab;
  });

  const contentOptionsNavigation = computed(() => {
    interface ContentOptions {
      name: string;
      value: "basic" | "premade" | "saved";
      disabled?: boolean;
      tooltip?: string;
    }

    const options: ContentOptions[] = [
      {
        name: t("blocks.empty_content"),
        value: "basic",
        tooltip: "",
      },
    ];

    if (type.value === "standalone") {
      options.push({
        name: "Pre-made",
        value: "premade",
        disabled: true,
        tooltip: "Only PRO version",
      });

      options.push({
        name: "Saved",
        value: "saved",
        disabled: true,
        tooltip: "Only PRO version",
      });
    } else {
      if (premadeBlocksStore.premadeBlocks !== false) {
        options.push({
          name: t("blocks.premade_content"),
          value: "premade",
          tooltip: "",
          disabled: false,
        });
      }

      if (
        savedBlocksStore.savedBlocks !== false ||
        savedBlocksStore.syncedSectionsEnabled
      ) {
        options.push({
          name: t("blocks.saved_content"),
          value: "saved",
          tooltip: "",
          disabled: false,
        });
      }
    }

    return options;
  });

  const visibleFileManager = computed(() => {
    return fileManagerStore.visibleFileManager;
  });

  const enableSyncedSections = computed(() => {
    return savedBlocksStore.syncedSectionsEnabled;
  });

  function changeActiveTab(id: number) {
    if (tabs.value) {
      tabs.value.activeTab = tabs.value.tabs[id];
    }
  }

  /*
   * Used for external file choose when customFileManager is enabled
   */
  function chooseFile(url: string) {
    // One-shot consume of fileIndex so a stale value from an abandoned open
    // cannot leak into this call.
    const childIndex = fileManagerStore.fileIndex;
    if (childIndex !== undefined) {
      fileManagerStore.fileIndex = undefined;
    }

    const value = url ? superEncodeURI(url) : null;

    if (mjmlStore.selectedBlock) {
      const block = mjmlStore.getSelectedBlock;

      if (
        block &&
        block.tagName === "mj-feed-block" &&
        childIndex !== undefined
      ) {
        mjmlStore.updateBlockItemAttribute({
          itemIndex: childIndex,
          prop: "image",
          value,
        });
      } else if (
        block &&
        (block.tagName === "mj-social" || block.tagName === "mj-carousel") &&
        childIndex !== undefined
      ) {
        mjmlStore.updateBlockChildrenAttribute({
          childIndex,
          prop: "src",
          value,
        });
      } else {
        mjmlStore.updateBlockAttribute({
          prop: "src",
          value,
        });
      }
    } else {
      mjmlStore.updateSectionAttribute({
        prop: "background-url",
        value,
      });
    }

    window.$event.emit("FILE_CHOSEN", url);
  }

  function clearSelection() {
    scroll.value.$el.scrollTop = 0;
    mjmlStore.clearSelected();
  }

  function registerListeners() {
    const globalEmitsHandler = window.$event;
    globalEmitsHandler.on("load", (e: any) => {
      loadJson(e.json);
    });
    globalEmitsHandler.on("togglePreview", () => {
      previewStore.tooglePreviewMode();
    });
    globalEmitsHandler.on("togglePreviewSize", () => {
      //toggle to desktop preview
      if (previewStore.previewSize === "375px") {
        previewStore.setPreviewSize("100%");
      }
      //toggle to mobile preview
      else {
        previewStore.setPreviewSize("375px");
      }
    });
    globalEmitsHandler.on("chooseFile", (url: string) => {
      chooseFile(url);
    });
    globalEmitsHandler.on("save", (lang?: string) => {
      save(lang);
    });
    globalEmitsHandler.on("undo", () => {
      mjmlStore.goBack();
    });
    globalEmitsHandler.on("redo", () => {
      mjmlStore.goForward();
    });
    globalEmitsHandler.on("saveSection", (block: unknown) => {
      saveBlock(block);
    });
    globalEmitsHandler.on("setSavedBlocks", (savedBlocks: any) => {
      savedBlocksStore.setSavedBlocks(savedBlocks);
    });
    globalEmitsHandler.on("deleteSavedBlock", (id: number) => {
      removeBlock(id);
    });
    globalEmitsHandler.on("editSavedBlock", (id: number) => {
      editBlock(id);
    });
    globalEmitsHandler.on("setPreviewHTML", (html: string) => {
      previewStore.setPreviewHtml(html);
    });
    globalEmitsHandler.on(
      "createNotification",
      (notification: INotification) => {
        notificationsStore.createNotification(notification);
      }
    );
    globalEmitsHandler.on("refreshComments", (key: string) => {
      refreshComments(key);
    });
    globalEmitsHandler.on("refreshSyncedSections", () => {
      updateSyncedSections();
    });
    globalEmitsHandler.on("openPremadeTemplatesSelection", () => {
      window.$event.emit("openTemplatePicker");
    });
    globalEmitsHandler.on(
      "updateApiAuthorizationHeader",
      (newHeader: string | Record<string, string>) => {
        setApiAuthorizationHeader(newHeader);
      }
    );
    globalEmitsHandler.on("setTemplateName", (name: string) => {
      generalStore.title = name;
    });
    globalEmitsHandler.on("updateOptions", (options: IOptions) => {
      if (!options) return;
      setOptions(options);
    });
    globalEmitsHandler.on("setMergeTags", (mergeTags: IMergeTagArray) => {
      mergeTagsStore.setMergeTags(mergeTags);
    });
    globalEmitsHandler.on(
      "selectPrimaryLanguage",
      async (
        props: SelectPrimaryBlockLanguage & {
          resolve: (lang: string | null) => void;
        }
      ) => {
        const result = await blockLanguageSelector.value.open(props);
        props.resolve(result);
      }
    );
    globalEmitsHandler.on("toggleChatAI", () => {
      chatAIStore.toggleOpenChat();
    });
    globalEmitsHandler.on("toggleAutosaves", () => {
      autosavesStore.toggleAutosaveMenu();
    });
    globalEmitsHandler.on("toggleComments", () => {
      commentsStore.toggleOpenComments();
    });
    globalEmitsHandler.on("createLanguage", (lang: string) => {
      window.$event.emit("createLang", lang);
    });
    globalEmitsHandler.on("setPrimaryLanguage", (lang: string) => {
      window.$event.emit("setPrimaryLang", lang);
    });
    globalEmitsHandler.on("deleteLanguage", (lang: string) => {
      window.$event.emit("deleteLang", lang);
    });
    globalEmitsHandler.on("selectLanguage", (lang: string) => {
      window.$event.emit("selectLang", lang);
    });
    globalEmitsHandler.on(
      "translateLanguage",
      (payload: { lang: string; sourceLang?: string }) => {
        openAiStore.translateSingleLanguage(payload.lang, payload.sourceLang);
      }
    );
    globalEmitsHandler.on("translateAllLanguages", () => {
      openAiStore.translateAllFromPrimary();
    });
    globalEmitsHandler.on("getMutations", () => {
      mjmlStore.sendMutationsToParent();
    });
    globalEmitsHandler.on("toggleControlPanel", () => {
      layoutStore.toggleControlPanel();
    });
    globalEmitsHandler.on(
      "updateTemplate",
      (payload: { json: any; options?: { skipSnapshot?: boolean } }) => {
        updateTemplate(payload.json, payload.options);
      }
    );
    globalEmitsHandler.on("toggleDarkMode", () => {
      previewStore.darkMode = !previewStore.darkMode;
    });
  }

  function saveBlock(block: unknown) {
    const message = {
      type: "callback",
      action: "onBlockSave",
      data: {
        args: [block],
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  }

  function removeBlock(id: number | string) {
    const message = {
      type: "callback",
      action: "onBlockRemove",
      data: {
        args: [id],
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  }

  function editBlock(id: number | string) {
    const message = {
      type: "callback",
      action: "onBlockEdit",
      data: {
        args: [id],
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  }

  function initialised() {
    const message = {
      type: "callback",
      action: "onInit",
      data: {
        args: null,
      },
    };

    window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
  }

  function onContentTabChanged(index: number) {
    selected.value = contentOptionsNavigation.value[index].value;
  }

  function contentTabActiveIndex() {
    const activeContentOption = contentOptionsNavigation.value.find(
      (item) => item.value === selected.value
    );
    if (activeContentOption)
      return contentOptionsNavigation.value.indexOf(activeContentOption);

    return 0;
  }

  watch(
    [alignRight, previewMode, activeTab, activeBlock, activeSection],
    (newVal) => {
      const body = document.body;

      if (newVal[0]) {
        body.classList.add("right");
      } else {
        body.classList.remove("right");
      }

      const html = document.getElementsByTagName("html");

      if (newVal[1]) {
        html[0].classList.add("overflow-y-hidden");
      } else {
        html[0].classList.remove("overflow-y-hidden");
      }

      if (newVal[2] || newVal[3] || newVal[4]) {
        if (scroll.value && scroll.value.$el) {
          scroll.value.$el.scrollTop = 0;
        }
      }
    }
  );

  watch(
    () => mjmlStore.getEdittedWithoutSave,
    (newValue) => {
      const message = {
        type: "callback",
        action: "onEdittedWithoutSaveChanged",
        data: {
          args: [newValue],
        },
      };
      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
    }
  );

  watch(enableSyncedSections, async (newVal) => {
    if (newVal) {
      await updateSyncedSections();
    }
  });

  onMounted(() => {
    registerListeners();

    if (type.value == "plugin") {
      // eslint-disable-next-line no-console
      console.log(
        "[TopolPlugin] Loaded - Awaiting TopolPlugin.load(json) call..."
      );
    }
    initialised();
  });

  watch(scrollContent, (el) => {
    scrollResizeObserver?.disconnect();
    scrollResizeObserver = null;
    if (el && typeof ResizeObserver !== "undefined") {
      scrollResizeObserver = new ResizeObserver(() => {
        scroll.value?.ps?.update();
      });
      scrollResizeObserver.observe(el);
    }
  });

  onBeforeUnmount(() => {
    scrollResizeObserver?.disconnect();
    scrollResizeObserver = null;
  });
</script>

<style lang="scss">
  /* Import colors and typography variables */
  @import "./css/all";

  /* Legacy CSS compatibilty */
  @import "./css/editor-css/defaults";

  #app {
    font-family: Inter, sans-serif;
  }

  .scroll-area {
    position: relative;
    margin: auto;
    height: v-bind(scrollHeight);
  }

  .editor-view {
    margin-left: 370px;
    position: relative;
    width: 100%;
  }

  .overflow-y-hidden {
    overflow-y: hidden;
  }
</style>
